import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, inputCls, labelCls, primaryBtn } from "@/components/AuthShell";

export default function Login() {
  const { login, continueAsGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      if (!user.email_verified) return navigate("/verify-email");
      if (user.banned) return navigate("/banned");
      navigate(location.state?.from || "/");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to buy, sell and message sellers."
      footer={<>New here? <Link to="/signup" className="font-semibold text-price hover:underline">Create an account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@example.com" />
        </div>
        <div>
          <label className={labelCls}>Password</label>
          <div className="relative">
            <input data-testid="login-password" type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-11`} placeholder="••••••••" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground" data-testid="login-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="mt-1.5 text-right">
            <Link to="/forgot-password" className="text-xs font-medium text-muted-foreground hover:text-price">Forgot password?</Link>
          </div>
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading} className={primaryBtn}>
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <button
        data-testid="guest-btn"
        onClick={() => { continueAsGuest(); navigate("/"); }}
        className="mt-3 w-full rounded-xl border border-border py-3 text-sm font-semibold text-foreground transition hover:bg-secondary"
      >
        Continue as guest
      </button>
    </AuthShell>
  );
}
